import { useMemo, useState, useEffect } from 'react';
import { useMomento } from './useMomento';
import { useActiveDomain } from '@/lib/hooks/useActiveDomain';
import { parseAgentSuggestions } from '@/lib/utils/parse-agent-suggestions';
import type { Suggestion } from '@/components/momento/SuggestedPromptsBar';

interface Goal {
  id: string;
  goal_type: 'frequency' | 'topic' | 'speaker';
  goal_config: {
    platform?: string;
    count?: number;
    period?: 'week' | 'month';
    theme?: string;
    keywords?: string[];
    speakerName?: string;
    intent?: 'feature_more' | 'establish_expert';
  };
  progress: {
    currentCount?: number;
  };
}

interface WorkflowOption {
  id: string;
  label: string;
  description?: string;
}

interface ToolResult {
  suspended?: boolean;
  suspendData?: {
    reason?: string;
    options?: WorkflowOption[];
  };
}

/**
 * Extract suggestions from suspended workflow options (highest priority)
 * When a workflow suspends with choices, those become the suggested actions
 */
function extractWorkflowSuggestions(toolResults?: ToolResult[]): Suggestion[] {
  if (!toolResults?.length) return [];

  for (const result of toolResults) {
    if (result?.suspended && result.suspendData?.options?.length) {
      return result.suspendData.options
        .slice(0, 4)
        .map(opt => ({
          prompt: opt.label,
          isWorkflowOption: true,
        }));
    }
  }
  return [];
}

function generateGoalSuggestions(goals: Goal[]): Suggestion[] {
  const suggestions: Suggestion[] = [];

  for (const goal of goals) {
    switch (goal.goal_type) {
      case 'frequency': {
        const current = goal.progress?.currentCount || 0;
        const target = goal.goal_config.count || 2;
        const remaining = target - current;

        if (remaining > 0) {
          const platform = goal.goal_config.platform || 'LinkedIn';
          suggestions.push({
            prompt: `Find a moment for my next ${platform} post`,
            isGoalBased: true,
            goalType: 'frequency',
          });
        }
        break;
      }

      case 'topic': {
        const theme = goal.goal_config.theme;
        if (theme) {
          suggestions.push({
            prompt: `Search for moments about ${theme}`,
            isGoalBased: true,
            goalType: 'topic',
          });
        }
        break;
      }

      case 'speaker': {
        const speakerName = goal.goal_config.speakerName;
        if (speakerName) {
          const action = goal.goal_config.intent === 'establish_expert'
            ? `Find ${speakerName}'s best insights`
            : `Show moments featuring ${speakerName}`;
          suggestions.push({
            prompt: action,
            isGoalBased: true,
            goalType: 'speaker',
          });
        }
        break;
      }
    }
  }

  return suggestions.slice(0, 2);
}

function generateContextSuggestions(
  selectedMoment: any,
  activeMemo: any
): Suggestion[] {
  if (activeMemo) {
    if (activeMemo.status === 'exported') {
      return [
        { prompt: "Download the exported video" },
        { prompt: "Create a LinkedIn post from this" },
      ];
    }

    if (activeMemo.status === 'exporting') {
      return [
        { prompt: "Check export progress" },
      ];
    }

    return [
      { prompt: "Remove filler words" },
      { prompt: "Export this memo" },
    ];
  }

  if (selectedMoment) {
    return [
      { prompt: "Create a post from this moment" },
      { prompt: "Export with captions" },
    ];
  }

  // Default suggestions when no context - rotate to show variety
  // Include speaker-related suggestions since they're key to content creation
  return [
    { prompt: "Search for testimonials" },
    { prompt: "Who are my speakers?" },
    { prompt: "Create a LinkedIn post" },
    { prompt: "Find recent videos" },
  ];
}

export function useGoalAwareSuggestions(
  lastMessageContent?: string,
  lastMessageToolResults?: ToolResult[]
): {
  suggestions: Suggestion[];
  goals: Goal[];
  isLoading: boolean;
} {
  const { selectedMoment, activeMemo } = useMomento();
  const { activeDomain } = useActiveDomain();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const domainId = activeDomain?.domain_id;

  useEffect(() => {
    if (!domainId) {
      setIsLoading(false);
      return;
    }

    const fetchGoals = async () => {
      try {
        const res = await fetch(`/api/momento/goals?domainId=${domainId}`);
        if (res.ok) {
          const data = await res.json();
          setGoals(data.goals || []);
        }
      } catch (error) {
        console.error('[useGoalAwareSuggestions] Failed to fetch goals:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGoals();
  }, [domainId]);

  const suggestions = useMemo(() => {
    console.log('[useGoalAwareSuggestions] Computing suggestions:', {
      hasLastMessageContent: !!lastMessageContent,
      lastMessagePreview: lastMessageContent?.slice(-300),
      goalCount: goals.length,
      hasSelectedMoment: !!selectedMoment,
      hasActiveMemo: !!activeMemo,
      toolResultCount: lastMessageToolResults?.length || 0,
    });

    // Priority 0: Workflow suspension options (highest priority)
    // When a workflow suspends with choices, those become the suggested actions
    const workflowSuggestions = extractWorkflowSuggestions(lastMessageToolResults);
    if (workflowSuggestions.length > 0) {
      console.log('[useGoalAwareSuggestions] Using workflow suggestions:', workflowSuggestions.map(s => s.prompt));
      return workflowSuggestions;
    }

    // Priority 1: Agent-generated suggestions (most contextual)
    const agentSuggestions = lastMessageContent
      ? parseAgentSuggestions(lastMessageContent).map(s => ({
          prompt: s.prompt,
          isGoalBased: false,
        }))
      : [];

    console.log('[useGoalAwareSuggestions] Agent suggestions:', agentSuggestions.map(s => s.prompt));

    // If agent provided suggestions, use those primarily
    if (agentSuggestions.length > 0) {
      // Add one goal suggestion if available to keep goals visible
      const goalSuggestions = generateGoalSuggestions(goals);
      if (goalSuggestions.length > 0) {
        const result = [...agentSuggestions.slice(0, 3), goalSuggestions[0]].slice(0, 4);
        console.log('[useGoalAwareSuggestions] Final suggestions (agent+goal):', result.map(s => s.prompt));
        return result;
      }
      console.log('[useGoalAwareSuggestions] Final suggestions (agent only):', agentSuggestions.slice(0, 4).map(s => s.prompt));
      return agentSuggestions.slice(0, 4);
    }

    // Fallback: Frontend-generated suggestions
    const goalSuggestions = generateGoalSuggestions(goals);
    const contextSuggestions = generateContextSuggestions(selectedMoment, activeMemo);

    // Balance goal vs context suggestions - show more context when fewer goals
    const combined: Suggestion[] = [];
    const goalCount = Math.min(goalSuggestions.length, 2);
    const contextCount = 4 - goalCount; // Fill remaining slots with context

    combined.push(...goalSuggestions.slice(0, goalCount));
    combined.push(...contextSuggestions.slice(0, contextCount));

    const result = combined.slice(0, 4);
    console.log('[useGoalAwareSuggestions] Final suggestions (fallback):', result.map(s => s.prompt));
    return result;
  }, [goals, selectedMoment, activeMemo, lastMessageContent, lastMessageToolResults]);

  return { suggestions, goals, isLoading };
}
